import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Loader, CheckCircle, AlertCircle } from 'lucide-react';

export default function UpdateBlogPostContent() {
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const slug = "why-non-profits-cant-afford-ignore-ai-any-longer";

  useEffect(() => {
    const updateBlogPost = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching blog post with slug:', slug);
        
        // First, fetch the current content
        const { data, error: fetchError } = await supabase
          .from('blog_posts')
          .select('content')
          .eq('slug', slug)
          .single();
        
        if (fetchError) {
          throw new Error(`Failed to fetch blog post: ${fetchError.message}`);
        }
        
        if (!data) {
          throw new Error(`Blog post with slug "${slug}" not found`);
        }
        
        // Check if the content already has the download section
        if (data.content.includes('nonprofit-ai-checklist-download')) {
          console.log('Download section already exists in the blog post');
          setSuccess(true);
          setLoading(false);
          return;
        }
        
        // Insert the download section before "Getting Started: You Don't Need a Tech Team"
        const targetHeading = '<h2>Getting Started: You Don\'t Need a Tech Team</h2>';
        
        if (!data.content.includes(targetHeading)) {
          throw new Error('Target heading not found in blog post content');
        }
        
        const downloadSection = `
<div class="my-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
  <h3 class="text-xl font-semibold text-gray-900 mb-4">Free Resource</h3>
  <p class="text-gray-600 mb-4">
    Get our comprehensive checklist to help your non-profit implement AI ethically and effectively.
  </p>
  <span id="nonprofit-ai-checklist-download">Download the Non-Profit AI Ethics Checklist (PDF)</span>
</div>
`;
        
        const updatedContent = data.content.replace(
          targetHeading, 
          `${downloadSection}\n\n${targetHeading}`
        );
        
        // Update the blog post content
        const { error: updateError } = await supabase
          .from('blog_posts')
          .update({ content: updatedContent })
          .eq('slug', slug);
        
        if (updateError) {
          throw new Error(`Failed to update blog post: ${updateError.message}`);
        }
        
        console.log('Blog post updated successfully');
        setSuccess(true);
      } catch (err) {
        console.error('Error updating blog post:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    updateBlogPost();
  }, []);
  
  return (
    <div className="p-6 max-w-md mx-auto bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Updating Blog Post Content</h2>
      
      {loading && (
        <div className="flex items-center justify-center py-4">
          <Loader className="h-8 w-8 text-[#F98B3D] animate-spin mr-3" />
          <p className="text-gray-600">Updating blog post content...</p>
        </div>
      )}
      
      {success && !loading && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-md">
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700">
                Blog post updated successfully! The download component has been added.
              </p>
              <p className="text-sm text-green-700 mt-2">
                Please refresh the blog post page to see the changes.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}